import { DateTime } from "luxon"
import fp from "lodash/fp"

import type { TFunction } from "i18next"
import type { GridColDef } from "@mui/x-data-grid"

import type {
  Powerline,
  PowerlineFrequency,
  PowerlinesTableRow,
  PowerlineValueCell,
} from "types/powerlines.types"
import type { DeviceRowInfo, DeviceWGroups, IDeviceWRelsById } from "types/device.types"

import { formatDateTime } from "helpers/utils/dateUtils"
import EntityCell from "widgets/common/EntityCell"
import { customValueFormatter } from "helpers/formatters/plots/plots"
import { formatCurrentSeasonalAmpacity } from "helpers/utils/seasonalAmpacity"
import { nameOrUnnamed } from "helpers/utils/translations"
import { renderDashIfEmpty } from "./tables"

export const formatAmpacity = (value: number, locale: string) =>
  customValueFormatter({
    suffix: "A",
    formatUnits: true,
    locale,
  })(value)

export const formatVoltage = (value: number, locale: string) =>
  customValueFormatter({
    suffix: "V",
    formatUnits: true,
    locale,
  })(value)

export const formatLength = (value: number, locale: string) =>
  customValueFormatter({
    suffix: "m",
    decimalPlaces: 2,
    formatUnits: true,
    locale,
  })(value)

/** A frequency of 0 means the line is DC, the rest are AC in hertz. */
export const formatFrequency = (value: PowerlineFrequency, locale: string) =>
  value === 0
    ? "DC"
    : customValueFormatter({
        suffix: "Hz",
        decimalPlaces: 0,
        formatUnits: false,
        locale,
      })(value)

/** conductor_ids supersedes conductor_id, which is all an older line carries. */
export const getConductorIds = (powerline: Powerline): number[] =>
  powerline.conductor_ids.length ? powerline.conductor_ids : [powerline.conductor_id]

/** Powerline attributes are all optional, so a cell without a value shows a dash. */
export const formatValueCell = (
  cell: PowerlineValueCell,
  format: (value: number, locale: string) => string,
) => (fp.isNil(cell?.value) ? "-" : format(cell.value, cell.locale))

// A missing value exports as an empty CSV cell; the screen shows a dash (renderDashIfEmpty).
const formatOptionalNumber = (
  value: number | null | undefined,
  format: (value: number, locale: string) => string,
  locale: string,
) => (fp.isNil(value) ? "" : format(value, locale))

// Search matches the raw number as well as its formatted text ("220000" and "220 kV").
const quickFilterOnNumber =
  (format: (value: number, locale: string) => string, locale: string) =>
  (search: string) => {
    const text = String(search).toLowerCase()
    return (value: number | null | undefined) =>
      !fp.isNil(value) &&
      (String(value).includes(text) || format(value, locale).toLowerCase().includes(text))
  }

export const getPowerlinesTableColumns = (
  baseOrgURL: string,
  t: TFunction,
  locale: string,
): GridColDef<PowerlinesTableRow>[] => {
  const numberColumn = (
    field: "nominalAmpacity" | "nominalVoltage" | "length",
    headerName: string,
    format: (value: number, locale: string) => string,
  ): GridColDef<PowerlinesTableRow> => ({
    field,
    headerName,
    flex: 1,
    minWidth: 130,
    valueFormatter: (value: number | null | undefined) =>
      formatOptionalNumber(value, format, locale),
    renderCell: renderDashIfEmpty,
    getApplyQuickFilterFn: quickFilterOnNumber(format, locale),
  })

  return [
    {
      field: "powerline",
      headerName: t("powerlines.POWERLINE"),
      flex: 1,
      minWidth: 220,
      valueGetter: (_value, row) => row.name ?? "",
      getApplyQuickFilterFn: (search) => {
        const text = String(search).toLowerCase()
        return (_value: string, row) =>
          nameOrUnnamed(row.name, t).toLowerCase().includes(text) ||
          String(row.id).includes(text)
      },
      renderCell: ({ row }) => (
        <EntityCell
          entity="powerline"
          id={row.id}
          name={row.name}
          baseOrgURL={baseOrgURL}
        />
      ),
    },
    // Exported, never shown: the power line column shows both in one cell.
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "name",
      headerName: t("powerlines.NAME"),
      valueFormatter: (value: string | null) => nameOrUnnamed(value, t),
    },
    {
      ...numberColumn(
        "nominalAmpacity",
        t("powerlines.AMPACITY_NOMINAL"),
        formatAmpacity,
      ),
      // Wide enough for a label like "1200 A (Dec 21 – Mar 21)".
      minWidth: 190,
      // The current seasonal rating shows its period on screen; the CSV keeps the number.
      renderCell: (params) => params.row.seasonalLabel ?? renderDashIfEmpty(params),
    },
    numberColumn("nominalVoltage", t("powerlines.VOLTAGE_NOMINAL"), formatVoltage),
    numberColumn("length", t("powerlines.LENGTH"), formatLength),
    {
      field: "updatedAt",
      headerName: t("powerlines.UPDATED_AT"),
      flex: 1,
      minWidth: 150,
      renderCell: renderDashIfEmpty,
    },
  ]
}

// The CSV keeps the id and name columns; the power line column itself is not exported.
export const POWERLINES_TABLE_CSV_FIELDS = [
  "id",
  "name",
  "nominalAmpacity",
  "nominalVoltage",
  "length",
  "updatedAt",
]

export const getPowerlinesTableRows = (
  powerlines: Powerline[],
  locale: string,
): PowerlinesTableRow[] => {
  return powerlines?.map((powerline) => {
    const seasonal = formatCurrentSeasonalAmpacity(powerline?.ampacity_seasonal, locale)
    return {
      id: powerline.id,
      name: powerline.name,
      nominalAmpacity: seasonal?.value ?? powerline.ampacity_nominal,
      seasonalLabel: seasonal?.label,
      nominalVoltage: powerline.voltage_nominal,
      length: powerline.length,
      updatedAt: powerline.updated_at
        ? formatDateTime(DateTime.fromISO(powerline.updated_at))
        : "",
    }
  })
}

export const getPowerlineAndTowerDevicesFromRow = (
  devices: DeviceRowInfo[],
  powerlineId: number,
  towerIds: Set<number>,
): DeviceRowInfo[] => {
  return devices.filter((entry) => {
    const settings = entry.device?.settings || {}
    if (settings.powerline_id === powerlineId) {
      return true
    }
    if (
      settings.installation_type === "tower" &&
      settings.tower_id &&
      towerIds.has(settings.tower_id)
    ) {
      return true
    }
    return false
  })
}

export const getDevicesByPowerlineIdFromRow = (
  devices: DeviceRowInfo[],
  powerlineId: number,
): DeviceRowInfo[] =>
  devices.filter((entry) => entry.device?.settings?.powerline_id === powerlineId)

export const getPowerlinesById = (
  powerlines: Powerline[],
): { [key: number]: Powerline } =>
  fp.indexBy((powerline: Powerline) => powerline.id)(powerlines)

export const checkPowerlinesByIdExistance = (
  powerlineIds: number[],
  powerlinesById: {
    [key: number]: Powerline
  },
): boolean => fp.every((id: number) => fp.has(id, powerlinesById))(powerlineIds)

export const getPowerlineDevicesIds = (
  devices: IDeviceWRelsById,
  powerlineId: number,
): number[] => {
  return fp.flow(
    fp.values,
    fp.filter(
      (d: DeviceWGroups) =>
        d.device.settings?.installation_type === "powerline" &&
        d.device?.settings?.powerline_id === powerlineId,
    ),
    fp.map((d: DeviceWGroups) => d.device.id),
  )(devices)
}

export const getPowerlineAndTowerDevicesIds = (
  devices: IDeviceWRelsById,
  powerlineId: number,
  towerIds: Set<number>,
): number[] => {
  return fp.flow(
    fp.values,
    fp.filter((d: DeviceWGroups) => {
      const settings = d.device?.settings
      return (
        (settings?.installation_type === "powerline" &&
          settings?.powerline_id === powerlineId) ||
        (settings?.installation_type === "tower" &&
          !!settings?.tower_id &&
          towerIds.has(settings.tower_id))
      )
    }),
    fp.map((d: DeviceWGroups) => d.device.id),
  )(devices)
}
